/**
 * Cabo game view.
 *
 * No traditional hand — cards belong to seats and sit on the table face-down.
 * The local player has lasting knowledge of some cards (initial peek + any
 * granted by abilities) which we render as face-up *to them only*. Other
 * players see only counts and face-down backs.
 *
 * Click semantics depend on the current turn state:
 *   - awaiting_action and *not* your turn: snap (try to match top of discard)
 *   - awaiting_action and your turn: snap, or use the action buttons (draw etc.)
 *   - drawn (your turn): swap with that pile slot, or click the drawn-card
 *     overlay's Discard
 *   - ability (your turn): pick a target valid for the active ability
 *   - give_after_snap (you're the snapper): pick one of your own to give back
 *
 * For clarity we only label clickable cards as such — the rest are dimmed.
 */

import { useEffect, useState } from "react";
import type {
  CaboCardId,
  CaboPublicState,
  CaboPrivateState,
} from "../../../../shared/games/cabo.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { PlayingCard } from "../../components/PlayingCard.js";

interface CaboGameProps {
  state: CaboPublicState;
  yourSeat: number | null;
  yourPrivate: CaboPrivateState | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

const CARD_W = 72;        // small cards — Cabo tables can hold a lot
const CARD_BIG_W = 100;   // drawn-card overlay / scores

export function CaboGame({ state, yourSeat, yourPrivate, events: _events, send, mobile = false }: CaboGameProps) {
  const isYourTurn = state.currentSeat === yourSeat;
  const turnState = state.turnState;
  // Eliminated players are out of the round — no snaps, no clicks. Their
  // turns are already skipped by the engine; this just neutralises the UI.
  const youAreOut =
    yourSeat !== null && (state.players[yourSeat]?.eliminated ?? false);

  // Local UI mode for what clicking a card means. Most of the time this is
  // dictated by the turn state, but during your own turn at awaiting_action
  // you might want "snap" vs "draw" — both are panel buttons, so we don't
  // need explicit modes there.
  type ClickIntent =
    | { kind: "none" }
    | { kind: "snap" }
    | { kind: "swap_drawn" }
    | { kind: "ability"; ability: "peek_own" | "peek_other" | "swap_blind" | "peek_two_and_maybe_swap" }
    | { kind: "give_after_snap" };
  const clickIntent: ClickIntent = (() => {
    if (youAreOut) return { kind: "none" };
    if (turnState.kind === "drawn" && isYourTurn) return { kind: "swap_drawn" };
    if (turnState.kind === "ability" && isYourTurn) {
      return { kind: "ability", ability: turnState.ability };
    }
    // give_after_snap belongs to the snapper, NOT the seat on the clock —
    // snaps can come from any seat at any time.
    if (turnState.kind === "give_after_snap" && turnState.snapperSeat === yourSeat) {
      return { kind: "give_after_snap" };
    }
    return { kind: "snap" };
  })();

  function onCardClick(targetSeat: number, cardIndex: number) {
    if (state.phase === "round_end" || state.phase === "initial_peek") return;
    switch (clickIntent.kind) {
      case "none":
        return;
      case "snap":
        send({
          t: "action",
          action: { kind: "cabo", payload: { type: "snap", targetSeat, targetCardIndex: cardIndex } },
        });
        return;
      case "swap_drawn":
        if (targetSeat !== yourSeat) return;
        send({
          t: "action",
          action: { kind: "cabo", payload: { type: "swap_drawn", cardIndex } },
        });
        return;
      case "ability":
        send({
          t: "action",
          action: {
            kind: "cabo",
            payload: { type: "ability_target", seat: targetSeat, cardIndex },
          },
        });
        return;
      case "give_after_snap":
        if (targetSeat !== yourSeat) return;
        send({
          t: "action",
          action: { kind: "cabo", payload: { type: "give_after_snap", cardIndex } },
        });
        return;
    }
  }

  return (
    <div className="cabo-view">
      <CaboHeaderBar state={state} yourSeat={yourSeat} />
      <CaboTablePiles
        state={state}
        yourSeat={yourSeat}
        yourPrivate={yourPrivate}
        clickIntent={clickIntent}
        onCardClick={onCardClick}
      />
      <CaboCenter state={state} mobile={mobile} />
      <SwapDiscardAnimation lastDrawnSwap={state.lastDrawnSwap} mobile={mobile} />
      <CaboActionBar state={state} yourSeat={yourSeat} send={send} mobile={mobile} />
      {yourPrivate && yourPrivate.drawnCard && isYourTurn && turnState.kind === "drawn" && (
        <DrawnCardPanel
          cardId={yourPrivate.drawnCard}
          onDiscard={() =>
            send({ t: "action", action: { kind: "cabo", payload: { type: "discard_drawn" } } })
          }
        />
      )}
      {state.phase === "initial_peek" && yourSeat !== null && (
        <InitialPeekReady state={state} yourSeat={yourSeat} send={send} />
      )}
      {state.phase === "round_end" && (
        <FinalScoresPanel state={state} yourSeat={yourSeat} send={send} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header bar
// ---------------------------------------------------------------------------

function CaboHeaderBar({
  state,
  yourSeat: _yourSeat,
}: {
  state: CaboPublicState;
  yourSeat: number | null;
}) {
  const phaseLabel = (() => {
    switch (state.phase) {
      case "initial_peek":
        return "Initial peek — review your bottom two cards";
      case "play":
        return `${state.players[state.currentSeat]?.displayName ?? "?"}'s turn`;
      case "cabo_called": {
        const caller = state.caboCalledBy !== null ? state.players[state.caboCalledBy]?.displayName : null;
        return `${caller ?? "Someone"} called Cabo · ${state.caboTurnsRemaining} turn(s) left`;
      }
      case "round_end":
        return "Round complete";
    }
  })();
  return (
    <div className="cabo-header">
      <div className="cabo-header-phase">{phaseLabel}</div>
      {state.lastEventText && (
        <div className="cabo-header-event">{state.lastEventText}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-seat card piles around the table
// ---------------------------------------------------------------------------

function CaboTablePiles({
  state,
  yourSeat,
  yourPrivate,
  clickIntent,
  onCardClick,
}: {
  state: CaboPublicState;
  yourSeat: number | null;
  yourPrivate: CaboPrivateState | null;
  clickIntent: { kind: string; ability?: string };
  onCardClick: (seat: number, idx: number) => void;
}) {
  // Build a set of slot keys that should currently be highlighted because
  // they just got swapped by a 10/J/Q/K ability. Highlight fades after ~2.5s.
  const swapHighlight = useSwapHighlight(state.lastSwap);
  // Same shape, but for the most recent peek (6/7/8/9 and Q/K picks) so
  // every seat at the table can see which card was looked at.
  const peekHighlight = usePeekHighlight(state.lastPeek);

  return (
    <div className="cabo-table">
      {state.players.map((p) => (
        <SeatPile
          key={p.seat}
          state={state}
          seat={p.seat}
          yourSeat={yourSeat}
          yourPrivate={yourPrivate}
          clickIntent={clickIntent}
          onCardClick={onCardClick}
          swapHighlight={swapHighlight}
          peekHighlight={peekHighlight}
        />
      ))}
    </div>
  );
}

/**
 * Returns a Set of "<seat>:<idx>" keys to highlight for a brief window
 * after each ability-driven swap. Re-renders to drop the highlight when
 * the window expires.
 */
const SWAP_HIGHLIGHT_MS = 5000;
function useSwapHighlight(lastSwap: CaboPublicState["lastSwap"]): Set<string> {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!lastSwap) return;
    const elapsed = Date.now() - lastSwap.at;
    if (elapsed >= SWAP_HIGHLIGHT_MS) return;
    const id = setTimeout(() => setNow(Date.now()), SWAP_HIGHLIGHT_MS - elapsed);
    return () => clearTimeout(id);
  }, [lastSwap?.at]);
  if (!lastSwap) return new Set();
  if (now - lastSwap.at > SWAP_HIGHLIGHT_MS) return new Set();
  return new Set([
    `${lastSwap.a.seat}:${lastSwap.a.cardIndex}`,
    `${lastSwap.b.seat}:${lastSwap.b.cardIndex}`,
  ]);
}

/**
 * One-shot fly-to-discard animation triggered when a player swaps their
 * drawn card with one on the table. Reads `lastDrawnSwap` from public
 * state, looks up the source slot and the discard pile via DOM queries,
 * then renders a copy of the discarded card that animates from the slot
 * to the pile. Purely visual — the underlying state has already updated
 * by the time this runs.
 */
const SWAP_DISCARD_ANIM_MS = 700;
function SwapDiscardAnimation({
  lastDrawnSwap,
  mobile,
}: {
  lastDrawnSwap: CaboPublicState["lastDrawnSwap"];
  mobile: boolean;
}) {
  const [flight, setFlight] = useState<
    | {
        cardId: CaboCardId;
        from: { x: number; y: number };
        dx: number;
        dy: number;
        width: number;
        key: number;
      }
    | null
  >(null);
  // Re-run only when a *new* swap arrives. Comparing `at` (the engine's
  // monotonic-ish timestamp) is sufficient — replays land with the same
  // timestamp and shouldn't re-trigger.
  useEffect(() => {
    if (!lastDrawnSwap) return;
    const slotSelector = `[data-cabo-slot="${lastDrawnSwap.seat}:${lastDrawnSwap.cardIndex}"]`;
    const fromEl = document.querySelector(slotSelector) as HTMLElement | null;
    const toEl = document.querySelector(".cabo-discard-pile") as HTMLElement | null;
    if (!fromEl || !toEl) return;
    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();
    // Centre-to-centre translation reads more naturally than top-left.
    const fromCx = fromRect.left + fromRect.width / 2;
    const fromCy = fromRect.top + fromRect.height / 2;
    const toCx = toRect.left + toRect.width / 2;
    const toCy = toRect.top + toRect.height / 2;
    // Render at the source's top-left and translate to centre delta.
    const width = mobile ? 60 : 72;
    setFlight({
      cardId: lastDrawnSwap.discarded,
      from: { x: fromCx - width / 2, y: fromCy - (width * 1.4) / 2 },
      dx: toCx - fromCx,
      dy: toCy - fromCy,
      width,
      key: lastDrawnSwap.at,
    });
    const id = setTimeout(() => setFlight(null), SWAP_DISCARD_ANIM_MS);
    return () => clearTimeout(id);
  }, [lastDrawnSwap?.at, mobile]);
  if (!flight) return null;
  return (
    <div
      key={flight.key}
      className="cabo-swap-flier"
      style={
        {
          left: `${flight.from.x}px`,
          top: `${flight.from.y}px`,
          "--cabo-fly-dx": `${flight.dx}px`,
          "--cabo-fly-dy": `${flight.dy}px`,
        } as React.CSSProperties
      }
    >
      <PlayingCard card={flight.cardId} width={flight.width} />
    </div>
  );
}

const PEEK_HIGHLIGHT_MS = 5000;
function usePeekHighlight(lastPeek: CaboPublicState["lastPeek"]): Set<string> {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!lastPeek) return;
    const elapsed = Date.now() - lastPeek.at;
    if (elapsed >= PEEK_HIGHLIGHT_MS) return;
    const id = setTimeout(() => setNow(Date.now()), PEEK_HIGHLIGHT_MS - elapsed);
    return () => clearTimeout(id);
  }, [lastPeek?.at]);
  if (!lastPeek) return new Set();
  if (now - lastPeek.at > PEEK_HIGHLIGHT_MS) return new Set();
  return new Set(lastPeek.cards.map((c) => `${c.seat}:${c.cardIndex}`));
}

function SeatPile({
  state,
  seat,
  yourSeat,
  yourPrivate,
  clickIntent,
  onCardClick,
  swapHighlight,
  peekHighlight,
}: {
  state: CaboPublicState;
  seat: number;
  yourSeat: number | null;
  yourPrivate: CaboPrivateState | null;
  clickIntent: { kind: string; ability?: string };
  onCardClick: (seat: number, idx: number) => void;
  swapHighlight: Set<string>;
  peekHighlight: Set<string>;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const isCurrent = state.currentSeat === seat;
  const isCabo = p.caboCalled;
  const isOut = p.eliminated;
  const slots = p.slots;

  // Build the set of slots currently exposed to me as face-up. Cabo is a
  // memory game — only "active" reveals show:
  //   - During initial_peek phase: my bottom 2 cards (initialPeekCards)
  //   - When I'm the actor holding a drawn card from a 6-K ability: the
  //     abilityRevealed entries on my private snapshot
  //   - Round-end: everyone's full hand from revealedHands
  const revealedHere: Record<number, CaboCardId> = {};
  if (state.phase === "round_end") {
    const hand = state.revealedHands?.[seat] ?? [];
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (c) revealedHere[i] = c;
    }
  } else if (yourPrivate) {
    if (isMe && yourPrivate.initialPeekCards) {
      for (const c of yourPrivate.initialPeekCards) revealedHere[c.cardIndex] = c.cardId;
    }
    for (const r of yourPrivate.abilityRevealed) {
      if (r.seat === seat) revealedHere[r.cardIndex] = r.cardId;
    }
  }

  return (
    <div className={`cabo-seat ${isMe ? "mine" : ""} ${isCurrent ? "turn" : ""} ${isCabo ? "cabo" : ""} ${isOut ? "out" : ""}`}>
      <div className="cabo-seat-name">
        <span className={`conn-dot ${p.connected ? "" : "off"}`} />
        {isMe && <span className="role-tag mine">YOU</span>}
        {isCurrent && <span className="role-tag mine" style={{ background: "var(--accent)", color: "#1a1a1a" }}>TURN</span>}
        {isCabo && <span className="role-tag mine" style={{ background: "#a06fb0", color: "#1a1a1a" }}>CABO</span>}
        {isOut && <span className="role-tag mine" style={{ background: "#888", color: "#1a1a1a" }}>OUT</span>}
        <span className="player-name">{p.displayName}</span>
      </div>
      <div className="cabo-seat-pile">
        {slots.map((occupied, idx) => {
          const showFaceUp = revealedHere[idx] ?? null;
          // Empty slot: render a placeholder; not clickable for any action.
          if (!occupied) {
            return (
              <div key={idx} className="cabo-card-wrap empty" title="Empty slot">
                <div
                  className="cabo-empty-slot"
                  style={{ width: CARD_W, height: Math.round(CARD_W * 1.4) }}
                />
                <span className="cabo-card-idx">{idx + 1}</span>
              </div>
            );
          }
          const cardClass = clickabilityClass(clickIntent, seat, yourSeat, isCabo, isOut);
          const swapped = swapHighlight.has(`${seat}:${idx}`);
          const peeked = peekHighlight.has(`${seat}:${idx}`);
          return (
            <div
              key={idx}
              className={`cabo-card-wrap ${cardClass} ${swapped ? "swapped" : ""} ${peeked ? "peeked" : ""}`}
              // `data-cabo-slot` is queried by the swap-discard animator so it
              // can compute the start position of the flying card.
              data-cabo-slot={`${seat}:${idx}`}
              onClick={() => {
                if (cardClass === "" || cardClass === "dim") return;
                onCardClick(seat, idx);
              }}
              title={cardClass === "snap" ? "Click to snap (must match top of discard)" : undefined}
            >
              <PlayingCard
                card={showFaceUp}
                faceDown={!showFaceUp}
                width={CARD_W}
              />
              <span className="cabo-card-idx">{idx + 1}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function clickabilityClass(
  intent: { kind: string; ability?: string },
  seat: number,
  yourSeat: number | null,
  isCabo: boolean,
  isOut: boolean,
): string {
  if (yourSeat === null) return ""; // spectator
  if (intent.kind === "none") return "dim"; // you are out — neutralise all cards
  if (isCabo) return "dim";
  // Eliminated opponents' cards can't be ability-targeted, but they CAN
  // still be snapped (their cards remain in play).
  if (isOut && intent.kind !== "snap") return "dim";
  switch (intent.kind) {
    case "snap":
      return "snap";
    case "swap_drawn":
      return seat === yourSeat ? "swap" : "dim";
    case "ability":
      switch (intent.ability) {
        case "peek_own":
          return seat === yourSeat ? "ability" : "dim";
        case "peek_other":
          return seat !== yourSeat ? "ability" : "dim";
        case "swap_blind":
        case "peek_two_and_maybe_swap":
          return "ability";
        default:
          return "";
      }
    case "give_after_snap":
      return seat === yourSeat ? "ability" : "dim";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Center pile (stockpile + discard)
// ---------------------------------------------------------------------------

function CaboCenter({ state, mobile }: { state: CaboPublicState; mobile: boolean }) {
  // Stockpile + discard cards shrink on phones so they don't dominate
  // the limited vertical real estate in the rotated landscape view.
  const w = mobile ? 68 : CARD_BIG_W;
  return (
    <div className="cabo-center">
      <div className="cabo-pile">
        <PlayingCard card={null} faceDown width={w} />
        <div className="cabo-pile-label">Stockpile · {state.stockpileCount}</div>
      </div>
      <div className="cabo-pile cabo-discard-pile">
        {state.discardTop ? (
          <PlayingCard card={state.discardTop} width={w} />
        ) : (
          <div className="cabo-pile-placeholder" style={{ width: w, height: Math.round(w * 1.4) }} />
        )}
        <div className="cabo-pile-label">Discard · {state.discardCount}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action bar (turn-specific buttons)
// ---------------------------------------------------------------------------

function CaboActionBar({
  state,
  yourSeat,
  send,
  mobile: _mobile,
}: {
  state: CaboPublicState;
  yourSeat: number | null;
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}) {
  if (yourSeat === null) return null;
  const ts = state.turnState;
  // Eliminated players are out — no actions, no snaps. Surface that clearly.
  if (state.players[yourSeat]?.eliminated) {
    return (
      <div className="action-bar">
        <span className="muted">
          You're <strong>out</strong> for this round — your cards still count for scoring.
        </span>
      </div>
    );
  }
  // give_after_snap belongs to the snapper, not the seat on the clock.
  if (ts.kind === "give_after_snap" && ts.snapperSeat === yourSeat) {
    return (
      <div className="action-bar">
        <span className="muted">
          Pick one of your own cards to give to{" "}
          <strong>{state.players[ts.targetSeat]?.displayName}</strong>.
        </span>
      </div>
    );
  }
  // While someone else's give-back is pending, surface that to the table so
  // it's clear why play is paused.
  if (ts.kind === "give_after_snap") {
    const snapper = state.players[ts.snapperSeat]?.displayName ?? `seat ${ts.snapperSeat}`;
    const target = state.players[ts.targetSeat]?.displayName ?? `seat ${ts.targetSeat}`;
    return (
      <div className="action-bar">
        <span className="muted">
          Waiting for <strong>{snapper}</strong> to give a card to{" "}
          <strong>{target}</strong>…
        </span>
      </div>
    );
  }
  if (state.currentSeat !== yourSeat) {
    return (
      <div className="action-bar">
        <span className="muted">
          Click any unprotected card on the table to <strong>snap</strong> it
          (must match the top of discard).
        </span>
      </div>
    );
  }
  if (ts.kind === "awaiting_action") {
    return (
      <div className="action-bar">
        <button
          className="primary"
          onClick={() =>
            send({ t: "action", action: { kind: "cabo", payload: { type: "draw_stockpile" } } })
          }
        >
          Draw from stockpile
        </button>
        {state.caboCalledBy === null && (
          <button
            onClick={() =>
              send({ t: "action", action: { kind: "cabo", payload: { type: "call_cabo" } } })
            }
            title="Lock in your score and force one final turn for everyone else"
          >
            Call Cabo
          </button>
        )}
      </div>
    );
  }
  if (ts.kind === "drawn") {
    return (
      <div className="action-bar">
        <span className="muted">
          Click one of your own cards to <strong>swap</strong> in the drawn
          card, or click <strong>Discard</strong> in the panel.
        </span>
      </div>
    );
  }
  if (ts.kind === "ability") {
    // After a 6/7 or 8/9 reveal, hold for the actor to click OK (auto-ack
    // on timeout). Show a countdown so the player knows how long they have.
    if ((ts.ability === "peek_own" || ts.ability === "peek_other") && ts.step === 1) {
      return (
        <div className="action-bar">
          <span className="muted">
            Card revealed — memorise it, then click <strong>OK</strong>.
          </span>
          <AckCountdownButton
            deadline={ts.ackDeadline ?? null}
            onAck={() =>
              send({ t: "action", action: { kind: "cabo", payload: { type: "ability_ack" } } })
            }
          />
        </div>
      );
    }
    const label = (() => {
      switch (ts.ability) {
        case "peek_own":
          return "Click one of your own cards to peek at it.";
        case "peek_other":
          return "Click another player's card to peek at it.";
        case "swap_blind":
          return ts.step === 0
            ? "Pick the first card to blindly swap."
            : "Pick the second card to blindly swap.";
        case "peek_two_and_maybe_swap":
          if (ts.step < 2) {
            return ts.step === 0 ? "Pick the first card to peek at." : "Pick the second card to peek at.";
          }
          return "";
      }
    })();
    return (
      <div className="action-bar">
        {label && <span className="muted">{label}</span>}
        {ts.ability === "peek_two_and_maybe_swap" && ts.step === 2 && (
          <>
            <button
              className="primary"
              onClick={() =>
                send({
                  t: "action",
                  action: { kind: "cabo", payload: { type: "ability_confirm_swap", doSwap: true } },
                })
              }
            >
              Swap them
            </button>
            <button
              onClick={() =>
                send({
                  t: "action",
                  action: { kind: "cabo", payload: { type: "ability_confirm_swap", doSwap: false } },
                })
              }
            >
              Leave them
            </button>
          </>
        )}
      </div>
    );
  }
  return null;
}

/** OK button that also shows a live countdown to the server's auto-ack. */
function AckCountdownButton({
  deadline,
  onAck,
}: {
  deadline: number | null;
  onAck: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);
  const remaining = deadline !== null ? Math.max(0, deadline - now) : 0;
  const secs = Math.ceil(remaining / 1000);
  return (
    <button className="primary" onClick={onAck}>
      OK{deadline !== null ? ` (${secs}s)` : ""}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Drawn card overlay
// ---------------------------------------------------------------------------

function DrawnCardPanel({ cardId, onDiscard }: { cardId: CaboCardId; onDiscard: () => void }) {
  return (
    <div className="cabo-drawn-panel">
      <div className="cabo-drawn-title">Your drawn card</div>
      <PlayingCard card={cardId} width={CARD_BIG_W} />
      <div className="cabo-drawn-actions">
        <button className="primary" onClick={onDiscard}>
          Discard
        </button>
        <div className="muted" style={{ fontSize: 12 }}>
          or click one of your cards to swap.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Initial peek panel
// ---------------------------------------------------------------------------

function InitialPeekReady({
  state,
  yourSeat,
  send,
}: {
  state: CaboPublicState;
  yourSeat: number;
  send: (m: ClientMsg) => void;
}) {
  const peekDone = state.peekDone?.[yourSeat] ?? false;
  const readyCount = state.peekDone?.filter((d) => d).length ?? 0;
  return (
    <div className="cabo-peek-ready">
      <div className="cabo-peek-ready-instructions">
        Memorize your two bottom cards (highlighted below).
      </div>
      <button
        className="primary cabo-peek-ready-button"
        disabled={peekDone}
        onClick={() =>
          send({ t: "action", action: { kind: "cabo", payload: { type: "peek_done" } } })
        }
      >
        {peekDone ? `Waiting · ${readyCount} / ${state.seatCount} ready` : "I'm Ready"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final scores
// ---------------------------------------------------------------------------

function FinalScoresPanel({
  state,
  yourSeat: _yourSeat,
  send,
}: {
  state: CaboPublicState;
  yourSeat: number | null;
  send: (m: ClientMsg) => void;
}) {
  const scores = state.finalScores ?? [];
  let best = Infinity;
  for (const s of scores) if (s < best) best = s;
  const [acked, setAcked] = useState(false);
  return (
    <div className="cabo-final-scores">
      <h3 style={{ marginTop: 0 }}>Round complete</h3>
      <div className="cabo-final-rows">
        {state.players.map((p, i) => {
          const score = scores[i] ?? 0;
          const isWinner = score === best;
          return (
            <div key={i} className={`cabo-final-row ${isWinner ? "winner" : ""}`}>
              <div className="cabo-final-name">
                {isWinner && <span className="role-tag mine">WIN</span>}
                {p.displayName}
              </div>
              <div className="cabo-final-cards">
                {(state.revealedHands?.[i] ?? []).map((cid, j) => (
                  <PlayingCard key={j} card={cid} width={CARD_W} />
                ))}
              </div>
              <div className="cabo-final-score">{score}</div>
            </div>
          );
        })}
      </div>
      <div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
        <button
          className="primary"
          disabled={acked}
          onClick={() => {
            setAcked(true);
            send({ t: "action", action: { kind: "cabo", payload: { type: "ready_next_round" } } });
          }}
        >
          {acked ? "Waiting for others…" : "Ready"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event log helper (used by GamePage options panel)
// ---------------------------------------------------------------------------

export function CaboEventLog({ events }: { events: GameEvent[] }) {
  const lines: string[] = [];
  for (const e of events.slice(-12)) {
    if (e.text) lines.push(e.text);
    else lines.push(e.type);
  }
  if (lines.length === 0) return null;
  return (
    <div className="event-log">
      {lines.map((l, i) => <div className="line" key={i}>{l}</div>)}
    </div>
  );
}

// Re-export useEffect to silence "imported but unused" if tree-shaking gets aggressive.
void useEffect;
